import { TourProvider } from '@reactour/tour';
import * as Sentry from '@sentry/react';
import { ServiceEventType } from '@ukpostbox/client-data';
import { i18n } from '@ukpostbox/frontend-framework';
import { MapLanguageToLocale } from '@ukpostbox/helpers';
import { enGB } from 'date-fns/locale';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { overviewTourSteps } from '../../Account/Tours/Overview';
import { UpdatesModal } from '../Components/UpdatesModal';

type SiteContactDetails = {
	open: boolean;
	departmentId?: number;
	placeholderMessage?: string;
	readOnlyMessage?: string;
};

interface SiteInterfaceProviderType {
	serviceAlertsData: Array<ServiceEventType> | null;

	setSettingsPage: (value: boolean) => void;
	setPdfViewer: (value: boolean, src?: string) => void;
	setServiceManager: (value: boolean) => void;
	setOnboarding: (value: boolean) => void;

	HandleError: (error: Error) => void;
	UpdateSiteContact: (
		state: boolean,
		departmentId?: number,
		placeholderMessage?: string,
		readOnlyMessage?: string,
	) => void;

	onboardingActive: boolean;
	tourFinished: boolean;

	serviceManagerOpen: boolean;
	messageThreadOpen?: boolean;

	siteContactDetails: SiteContactDetails;
	pDFViewerDetails?: any;

	settingsPageOpen?: boolean;

	AddViewedAlertId: any;

	currentLocale: any;
	currentLanguage: string;
	updateLanguage: (locale: string) => void;

	updatePageMeta: (data: PageMetaType) => void;

	LOCAL_STORAGE_KEYS: Record<string, string>;
	sentryErrorId: string;

	updatesModalOpen: boolean;
	setUpdatesModal: (value: boolean) => void;
}

type PageMetaType = {
	title: string;
	description?: string;
};

const SiteInterfaceContext = React.createContext<SiteInterfaceProviderType>({} as SiteInterfaceProviderType);

function SiteInterfaceProvider(props: any) {
	const redirect = useNavigate();
	const [serviceAlertsData, setServiceAlertsData] = useState<Array<ServiceEventType> | null>(null);

	const [serviceManagerOpen, setServiceManagerOpen] = useState<boolean>(false);
	const [onboardingActive, setOnboardingActive] = useState<boolean>(false);

	const [tourStep, setTourStep] = useState(0);
	const [tourFinished, setTourFinished] = useState<boolean>(false);
	const [siteContactDetails, setSiteContactDetails] = useState<SiteContactDetails>({ open: false });
	const [pDFViewerDetails, setPDFViewerDetails] = useState<any>({ open: false, uri: '' });
	const [settingsPageOpen, setSettingsPageOpen] = useState<boolean>(false);

	const [updatesModalOpen, setUpdatesModalOpen] = useState<boolean>(false);

	const [currentLanguage, setCurrentLanguage] = useState<string>('en');
	const [currentLocale, setCurrentLocale] = useState(enGB);

	const [sentryErrorId, setSentryErrorId] = useState<string>('');

	const LOCAL_STORAGE_KEYS = {
		ONBOARDING: `${props.storageKey}-onboarding`,
		LANGUAGE: `${props.storageKey}-language`,
		EVENTS: `${props.storageKey}-viewed-event-ids`,
	};

	const setSettingsPage = (value: boolean) => {
		setSettingsPageOpen(value);
	};

	const UpdateSiteContact = (
		state: boolean,
		departmentId?: number,
		placeholderMessage?: string,
		readOnlyMessage?: string,
	) => {
		setSiteContactDetails({ open: state, departmentId, placeholderMessage, readOnlyMessage });
	};

	const setPdfViewer = (value: boolean, src?: string) => {
		setPDFViewerDetails({ open: value, uri: src || '' });
	};

	const setServiceManager = (value: boolean) => {
		setServiceManagerOpen(value);
	};

	const setUpdatesModal = (value: boolean) => {
		setUpdatesModalOpen(value);
	};

	const setOnboarding = (value: boolean) => {
		localStorage.setItem(LOCAL_STORAGE_KEYS.ONBOARDING, value.toString());
		setOnboardingActive(value);
	};

	const updateLanguage = (locale: string) => {
		i18n.changeLanguage(locale);
		localStorage.setItem(LOCAL_STORAGE_KEYS.LANGUAGE, locale);
		setCurrentLocale(MapLanguageToLocale(locale));
	};

	const AddViewedAlertId = (value: number) => {
		const viewedIds = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.EVENTS) || '[]');
		const newIds = [...viewedIds, value];
		localStorage.setItem(LOCAL_STORAGE_KEYS.EVENTS, JSON.stringify(newIds));
	};

	const updatePageMeta = (data: PageMetaType) => {
		document.title = data.title;
		if (data.description) {
			document.querySelector('meta[name="description"]')?.setAttribute('content', data.description);
		}

		// ReactGA.pageview(window.location.pathname + window.location.search);
	};

	const handleTourStepChange = (step: any) => {
		switch (step) {
			case 2:
				redirect('/mail/inbox');
				break;
			default:
				redirect('/');
				break;
		}

		setTourStep(step);
	};

	const HandleError = (error: Error) => {
		if (process.env.NODE_ENV === 'production') {
			setSentryErrorId(Sentry.captureException(error));
		}
	};

	useEffect(() => {
		if (localStorage.getItem(LOCAL_STORAGE_KEYS.ONBOARDING)) {
			setOnboarding(localStorage.getItem(LOCAL_STORAGE_KEYS.ONBOARDING) === 'true');
		}
		if (localStorage.getItem(LOCAL_STORAGE_KEYS.LANGUAGE)) {
			updateLanguage(localStorage.getItem(LOCAL_STORAGE_KEYS.LANGUAGE) as string);
		}
	}, []);

	const siteMemo = useMemo(
		() => ({
			serviceAlertsData,

			setSettingsPage,
			setPdfViewer,
			setServiceManager,
			setOnboarding,

			UpdateSiteContact,
			HandleError,

			currentLocale,
			currentLanguage,
			onboardingActive,
			tourFinished,
			serviceManagerOpen,
			settingsPageOpen,
			siteContactDetails,
			pDFViewerDetails,

			updateLanguage,
			updatePageMeta,

			AddViewedAlertId,

			LOCAL_STORAGE_KEYS,
			sentryErrorId,

			updatesModalOpen,
			setUpdatesModal,
		}),
		[
			LOCAL_STORAGE_KEYS,
			updatesModalOpen,
			sentryErrorId,
			currentLocale,
			currentLanguage,
			onboardingActive,
			tourFinished,
			serviceAlertsData,
			serviceManagerOpen,
			settingsPageOpen,
			pDFViewerDetails,
			siteContactDetails,
		],
	);

	return (
		<SiteInterfaceContext.Provider value={siteMemo}>
			<TourProvider
				steps={overviewTourSteps}
				padding={0}
				currentStep={tourStep}
				setCurrentStep={(step) => handleTourStepChange(step)}
				className='surface-wrapper'
				onClickClose={(e) => {
					e.setIsOpen(false);
					setTourFinished(true);
				}}
			>
				{props.children}
				<UpdatesModal isOpen={updatesModalOpen} closeCallback={() => setUpdatesModalOpen(false)} />
			</TourProvider>
		</SiteInterfaceContext.Provider>
	);
}

const useSite = () => useContext(SiteInterfaceContext);

export { SiteInterfaceContext, useSite };

export default SiteInterfaceProvider;
